// src/pages/tickets/components/mobile/TicketsMobile.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useMobileNavStore } from '@/stores/mobileNavStore';
import { MobileSearchBar, MobileListSkeleton, MobileEmptyState } from '@/components/layout/mobile';
import { TicketCard } from './TicketCard';
import type { Ticket, EstadoTicket } from '@/types';

interface TicketsMobileProps {
  tickets: Ticket[];
  isLoading: boolean;
}

const QUICK_FILTERS: { key: EstadoTicket | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'NUEVO', label: 'Nuevos' },
  { key: 'EN_ANALISIS', label: 'Análisis' },
  { key: 'EN_DESARROLLO', label: 'Desarrollo' },
  { key: 'RESUELTO', label: 'Resueltos' },
];

export function TicketsMobile({
  tickets,
  isLoading,
}: TicketsMobileProps) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [quickFilter, setQuickFilter] = useState<EstadoTicket | 'all'>('all');
  const { setNavigation } = useMobileNavStore();

  useEffect(() => {
    setNavigation({ title: 'Tickets' });
  }, [setNavigation]);

  // Filter
  let visible = tickets;
  if (quickFilter !== 'all') {
    visible = visible.filter((t) => t.estado === quickFilter);
  }
  if (searchText) {
    const q = searchText.toLowerCase();
    visible = visible.filter(
      (t) =>
        t.titulo.toLowerCase().includes(q) ||
        (t.numero ?? '').toLowerCase().includes(q) ||
        t.cliente?.nombre?.toLowerCase().includes(q)
    );
  }

  const emptyMessage = searchText ? 'No se encontraron tickets' : 'No hay tickets';

  return (
    <div className="flex flex-col h-full">
      {/* Quick filter chips (horizontal scroll) */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border overflow-x-auto scrollbar-none">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setQuickFilter(f.key)}
            className={cn(
              'shrink-0 text-xs font-medium px-3 py-1.5 rounded-full transition-colors',
              quickFilter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground active:bg-muted/80'
            )}
          >
            {f.label}
          </button>
        ))}

        <div className="flex-1" />

        <Button
          variant={searchOpen ? 'default' : 'ghost'}
          size="sm"
          className="h-8 px-2 shrink-0"
          onClick={() => {
            setSearchOpen(!searchOpen);
            if (searchOpen) setSearchText('');
          }}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <MobileSearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Buscar ticket..."
        />
      )}

      {/* Ticket list */}
      <div className="flex-1 overflow-y-auto overscroll-y-contain">
        {isLoading ? (
          <MobileListSkeleton itemHeight="h-[80px]" />
        ) : visible.length === 0 ? (
          <MobileEmptyState message={emptyMessage} />
        ) : (
          <div>
            <div className="px-4 py-2 text-xs text-muted-foreground">
              {visible.length} ticket{visible.length !== 1 ? 's' : ''}
            </div>
            {visible.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                onTap={(id) => navigate(`/tickets/${id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
